import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Honeypot from "@/components/Honeypot";
import { z } from "zod";

const schema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z.string().trim().min(5, "Please enter a valid phone").max(40),
  buyBox: z.string().trim().max(1000),
});

const LeadForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", buyBox: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hp, setHp] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: "" }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hp.trim()) { setSent(true); return; }
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      parsed.error.issues.forEach((i) => { fe[i.path[0] as string] = i.message; });
      setErrors(fe);
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("contact-ai", {
        body: {
          name: parsed.data.name, email: parsed.data.email, phone: parsed.data.phone,
          company: "", businessType: "Real Estate Investor",
          message: `Market / Buy Box: ${parsed.data.buyBox}\n\nRequested from homepage final CTA.`,
          _hp: hp,
        },
      });
      if (error) throw error;
      setSent(true);
      setForm({ name: "", email: "", phone: "", buyBox: "" });
      toast({ title: "Got it.", description: "We'll reach out within one business day." });
    } catch {
      toast({
        title: "We couldn't send that",
        description: "Please try again in a moment or email abdelrahaman@elevateglobalsolutions.net.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-secondary/30 bg-white/5 p-8 text-center backdrop-blur">
        <h3 className="font-display text-xl font-bold text-primary-foreground">You're on the calendar list.</h3>
        <p className="mt-2 text-sm text-primary-foreground/90">
          We'll email you within 1 business day with a Calendly link and a quick prep doc.
        </p>
      </div>
    );
  }

  const inputCls = "border-white/15 bg-white/5 text-primary-foreground placeholder:text-primary-foreground/50";

  return (
    <form onSubmit={onSubmit} noValidate className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur md:p-8">
      <Honeypot value={hp} onChange={setHp} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Input required name="name" value={form.name} onChange={onChange} placeholder="Full name"
            aria-invalid={!!errors.name} className={inputCls} />
          {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <Input required type="email" name="email" value={form.email} onChange={onChange} placeholder="Work email"
            aria-invalid={!!errors.email} className={inputCls} />
          {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
        </div>
        <div className="sm:col-span-2">
          <Input required name="phone" value={form.phone} onChange={onChange} placeholder="Phone (US)"
            aria-invalid={!!errors.phone} className={inputCls} />
          {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
        </div>
        <Textarea name="buyBox" value={form.buyBox} onChange={onChange}
          placeholder="Market & buy box (e.g. DFW, SFR 80, 250k, off-market wholesale)" rows={3}
          className={`${inputCls} sm:col-span-2`} />
      </div>
      <Button type="submit" size="lg" disabled={loading}
        className="mt-4 w-full bg-secondary text-secondary-foreground hover:bg-secondary/90">
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Book My Free Strategy Call
        {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
      </Button>
      <p className="mt-3 text-center text-xs text-primary-foreground/85">
        30 minutes. No contracts. We respond within 1 business day.
      </p>
    </form>
  );
};

export default LeadForm;
